/** @fileoverview Searches the values of one system field page by page for a choice input, and finds the label of the picked value. */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { FabrikFieldOption, FabrikInputValue, FabrikSystemField } from '../../types/apps';
import { useFieldValuesReader } from './appRuntimeContext';
import { MAX_CODE_LENGTH, uniqueByValue } from './fieldOptions';

/* Values asked for per page, how long typing waits before it searches, and the longest search. */
const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;
const MAX_SEARCH_LENGTH = 100;

type SearchPages = {
  /** The search the loaded pages answer; null before the first page arrives. */
  search: string | null;
  options: FabrikFieldOption[];
  pageCount: number;
  hasMore: boolean;
  loading: boolean;
  error: string;
};
const NO_PAGES: SearchPages = {
  search: null,
  options: [],
  pageCount: 0,
  hasMore: false,
  loading: false,
  error: '',
};

export type FieldValuesSearch = {
  options: FabrikFieldOption[];
  loading: boolean;
  hasMore: boolean;
  error: string;
  searchText: string;
  /** True when the loaded options answer the current search text and nothing is loading. */
  optionsMatchSearchText: boolean;
  setSearchText: (text: string) => void;
  /** Tells the search whether the dropdown is open; the first page loads when it opens. */
  setOpen: (isOpen: boolean) => void;
  /** Loads the next page of the current search, when there is one. */
  loadMore: () => void;
  /** Label of the picked value, or the value itself until its label is known. */
  selectedLabel: string | undefined;
};

/**
 * Searches one system field's values on the server: the first page when the dropdown opens, a
 * new search a moment after typing stops, and the next page on request. Answers to an older
 * search are ignored. The picked value's label is looked up when no loaded page holds it.
 * Does nothing while `systemField` is undefined.
 * @param systemField the field to search, or undefined when the input does not search
 * @param value the picked value
 */
export const useFieldValuesSearch = (
  systemField: FabrikSystemField | undefined,
  value: FabrikInputValue | undefined
): FieldValuesSearch => {
  const readFieldValues = useFieldValuesReader();
  const { systemId, entity, field } = systemField ?? ({} as Partial<FabrikSystemField>);
  const [searchText, setSearchText] = useState('');
  const [wantedSearch, setWantedSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [pages, setPages] = useState<SearchPages>(NO_PAGES);
  /* Labels of every value seen on a page or looked up, so the picked value keeps its label. */
  const [labels, setLabels] = useState<Map<string, string>>(new Map());
  const latestRequest = useRef(0);
  const requestedSearch = useRef<string | null>(null);

  /* A different field starts over. */
  useEffect(() => {
    latestRequest.current += 1;
    requestedSearch.current = null;
    setPages(NO_PAGES);
    setLabels(new Map());
  }, [systemId, entity, field]);

  /* Typing waits a moment before it searches, so each key press does not send a call. */
  useEffect(() => {
    const timer = setTimeout(
      () => setWantedSearch(searchText.trim().slice(0, MAX_SEARCH_LENGTH)),
      SEARCH_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [searchText]);

  /**
   * Reads one page of a search. The first page replaces the loaded options, later pages add to
   * them; the old options stay while a page loads.
   * @param search the search text
   * @param pageIndex the page to read, from 0
   */
  const loadPage = useCallback(
    (search: string, pageIndex: number) => {
      if (!systemId || !entity || !field) {
        return;
      }
      latestRequest.current += 1;
      const requestNumber = latestRequest.current;
      if (pageIndex === 0) {
        requestedSearch.current = search;
      }
      setPages((previous) => ({ ...previous, loading: true, error: '' }));
      readFieldValues({
        systemId,
        entity,
        field,
        search,
        top: PAGE_SIZE,
        skip: pageIndex * PAGE_SIZE,
      })
        .then(({ results, hasMore }) => {
          if (requestNumber !== latestRequest.current) return;
          setPages((previous) => ({
            search,
            options: uniqueByValue(pageIndex === 0 ? results : [...previous.options, ...results]),
            pageCount: pageIndex + 1,
            hasMore,
            loading: false,
            error: '',
          }));
          setLabels((previous) => {
            const next = new Map(previous);
            results.forEach(({ value: code, label }) => next.set(code, label));
            return next;
          });
        })
        .catch((reason: unknown) => {
          if (requestNumber !== latestRequest.current) return;
          if (pageIndex === 0) {
            requestedSearch.current = null;
          }
          const message = reason instanceof Error ? reason.message : 'Unable to load options.';
          setPages((previous) => ({ ...previous, loading: false, error: message }));
        });
    },
    [entity, field, readFieldValues, systemId]
  );

  /* An open dropdown shows the first page of the current search. */
  useEffect(() => {
    if (isOpen && requestedSearch.current !== wantedSearch) {
      loadPage(wantedSearch, 0);
    }
  }, [isOpen, loadPage, wantedSearch]);

  const code =
    value === undefined || value === '' || Array.isArray(value) ? undefined : String(value);

  /* A value no loaded page holds (a form opened with a value) gets its label looked up. */
  useEffect(() => {
    if (!systemId || !entity || !field || code === undefined || code.length > MAX_CODE_LENGTH) {
      return undefined;
    }
    if (labels.has(code)) {
      return undefined;
    }
    let isCurrent = true;
    readFieldValues({ systemId, entity, field, values: [code] })
      .then(({ results }) => {
        const found = results.find(({ value: resultCode }) => resultCode === code);
        if (isCurrent && found) {
          setLabels((previous) => new Map(previous).set(code, found.label));
        }
      })
      .catch(() => undefined);
    return () => {
      isCurrent = false;
    };
  }, [code, entity, field, labels, readFieldValues, systemId]);

  /**
   * Opens or closes the search. Closing clears the search text, so the next open starts from
   * the whole list again.
   * @param nextOpen whether the dropdown is open
   */
  const setOpen = useCallback((nextOpen: boolean) => {
    setIsOpen(nextOpen);
    if (!nextOpen) {
      setSearchText('');
      setWantedSearch('');
    }
  }, []);

  const { search, options, pageCount, hasMore, loading, error } = pages;
  const loadMore = useCallback(() => {
    if (!loading && hasMore && search !== null) {
      loadPage(search, pageCount);
    }
  }, [hasMore, loadPage, loading, pageCount, search]);

  return {
    options,
    loading,
    hasMore,
    error,
    searchText,
    optionsMatchSearchText: !loading && search === searchText.trim().slice(0, MAX_SEARCH_LENGTH),
    setSearchText,
    setOpen,
    loadMore,
    selectedLabel: code === undefined ? undefined : labels.get(code) ?? code,
  };
};
